import { useState, useCallback } from 'react'
import { useBulkDeviceAssignment } from './use-bulk-device-assignment'
import useAnsibleCapacityUpdate from './use-ansible-capacity-update'
import useMacMiniAssignmentWithNaming from './use-mac-mini-assignment-with-naming'
import usePhysicalPhoneAssignmentWithNaming from './use-physical-phone-assignment-with-naming'
import { Ansible } from 'interfaces/ansible'
import { PnsMacMini } from 'interfaces/pns-mac-mini'
import { PhysicalPhone } from 'interfaces/physical-phone'
import toast from 'react-hot-toast'

interface Device {
  id: string
  name: string
  type: 'mac-mini' | 'physical-phone'
}

interface UseDeviceAssignmentFlowReturn {
  assignDevicesToAnsible: (
    devices: Device[],
    ansible: Ansible,
    terminalId: string
  ) => Promise<void>
  isAssigning: boolean
  assignmentProgress: {
    current: number
    total: number
    currentAction: string
  } | null
}

export const useDeviceAssignmentFlow = (): UseDeviceAssignmentFlowReturn => {
  const [isAssigning, setIsAssigning] = useState(false)
  const [assignmentProgress, setAssignmentProgress] = useState<{
    current: number
    total: number
    currentAction: string
  } | null>(null)

  const bulkAssignMutation = useBulkDeviceAssignment()
  const capacityUpdateMutation = useAnsibleCapacityUpdate()
  const macMiniAssignmentWithNaming = useMacMiniAssignmentWithNaming()
  const phoneAssignmentWithNaming = usePhysicalPhoneAssignmentWithNaming()

  const assignDevicesToAnsible = useCallback(
    async (devices: Device[], ansible: Ansible, terminalId: string) => {
      if (devices.length === 0) {
        toast.error('No devices selected for assignment')
        return
      }

      setIsAssigning(true)
      setAssignmentProgress({
        current: 0,
        total: 2, // Device assignment + capacity update
        currentAction: 'Assigning devices to ansible...',
      })

      try {
        // Separate Mac Minis from Physical Phones
        const macMinis = devices.filter((d) => d.type === 'mac-mini')
        const physicalPhones = devices.filter(
          (d) => d.type === 'physical-phone'
        )

        let totalSteps = 1 // Capacity update step
        if (macMinis.length > 0) totalSteps += 1 // Mac Mini assignment with naming
        if (physicalPhones.length > 0) totalSteps += 1 // Physical Phone assignment with naming

        let currentStep = 0

        setAssignmentProgress({
          current: currentStep,
          total: totalSteps,
          currentAction: 'Starting device assignment...',
        })

        // Step 1: Handle Mac Minis with auto-naming
        if (macMinis.length > 0) {
          setAssignmentProgress({
            current: currentStep,
            total: totalSteps,
            currentAction: 'Assigning Mac Minis with auto-naming...',
          })

          // Convert Device[] to PnsMacMini[] for the naming hook
          const macMiniEntities: PnsMacMini[] = macMinis.map((device) => ({
            id: device.id,
            name: device.name,
            // Add other required fields with defaults - the hook will update them
            model: '',
            serialNumber: '',
            macAddress: '',
            osVersion: '',
            maxUsers: 0,
            currentUsers: 0,
            status: 'RECEIVED' as any,
            userIds: [],
            availableUserSlots: 0,
            isAvailable: false,
            createdAt: '',
            updatedAt: '',
          }))

          await macMiniAssignmentWithNaming.assignMacMinisToAnsible(
            macMiniEntities,
            ansible,
            terminalId
          )

          currentStep += 1
        }

        // Step 2: Handle Physical Phones with auto-naming
        if (physicalPhones.length > 0) {
          setAssignmentProgress({
            current: currentStep,
            total: totalSteps,
            currentAction: 'Assigning Physical Phones with auto-naming...',
          })

          // Convert Device[] to PhysicalPhone[] for the naming hook
          const phoneEntities: PhysicalPhone[] = physicalPhones.map(
            (device) => ({
              id: device.id,
              name: device.name,
              // Add other required fields with defaults - the hook will update them
              deviceModel: '',
              serialNumber: '',
              status: 'RECEIVED' as any,
              isAssigned: false,
              isAvailable: false,
              createdAt: '',
              updatedAt: '',
            })
          )

          await phoneAssignmentWithNaming.assignPhonesToAnsible(
            phoneEntities,
            ansible,
            terminalId
          )

          currentStep += 1
        }

        // Step 3: Update ansible capacity counts
        setAssignmentProgress({
          current: currentStep,
          total: totalSteps,
          currentAction: 'Updating ansible capacity...',
        })

        await capacityUpdateMutation.mutateAsync({
          ansibleId: ansible.id,
          currentMacMinis: ansible.currentMacMinis + macMinis.length,
          currentPhysicalPhones:
            ansible.currentPhysicalPhones + physicalPhones.length,
        })

        setAssignmentProgress({
          current: totalSteps,
          total: totalSteps,
          currentAction: 'Assignment completed successfully!',
        })

        toast.success(
          `Successfully assigned ${devices.length} device${
            devices.length === 1 ? '' : 's'
          } to ${ansible.name} with auto-naming`
        )

        // Clear progress after a short delay
        setTimeout(() => {
          setAssignmentProgress(null)
        }, 2000)
      } catch (error) {
        console.error('Assignment flow failed:', error)

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unknown error occurred during assignment'

        toast.error(`Assignment failed: ${errorMessage}`)

        setAssignmentProgress(null)
        throw error
      } finally {
        setIsAssigning(false)
      }
    },
    [
      macMiniAssignmentWithNaming,
      phoneAssignmentWithNaming,
      capacityUpdateMutation,
    ]
  )

  return {
    assignDevicesToAnsible,
    isAssigning:
      isAssigning ||
      bulkAssignMutation.isLoading ||
      capacityUpdateMutation.isLoading,
    assignmentProgress,
  }
}
